import { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { surgeryORConsoleRoute } from '@vitvio/navigation';
import { ConfirmPatientModal } from '@vitvio/shared-components';
import type { ConfirmPatientModalProps } from '@vitvio/shared-components';

type ConfirmPatientModalData = NonNullable<ConfirmPatientModalProps['data']>;

interface ModalState {
  data: ConfirmPatientModalData;
  surgeryId: string;
}

interface ConfirmPatientModalContextType {
  openConfirmModal: (data: ConfirmPatientModalData, surgeryId: string) => void;
}

const ConfirmPatientModalContext = createContext<ConfirmPatientModalContextType | null>(null);

interface ConfirmPatientModalProviderProps {
  children: React.ReactNode;
}

function ConfirmPatientModalProvider({ children }: ConfirmPatientModalProviderProps) {
  const navigate = useNavigate();
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const openConfirmModal = useCallback((data: ConfirmPatientModalData, surgeryId: string) => {
    setModalState({ data, surgeryId });
  }, []);

  const handleConfirm = useCallback(() => {
    if (modalState) {
      navigate(surgeryORConsoleRoute(modalState.surgeryId));
    }
    setModalState(null);
  }, [modalState, navigate]);

  const handleCancel = useCallback(() => {
    setModalState(null);
  }, []);

  return (
    <ConfirmPatientModalContext.Provider value={{ openConfirmModal }}>
      {children}
      <ConfirmPatientModal
        isOpen={modalState !== null}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        data={modalState?.data}
      />
    </ConfirmPatientModalContext.Provider>
  );
}

function useConfirmPatientModal() {
  const context = useContext(ConfirmPatientModalContext);
  if (context === null) throw new Error('useConfirmPatientModal was used outside ConfirmPatientModalProvider.');
  return context;
}

export { useConfirmPatientModal, ConfirmPatientModalProvider };
